import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Award,
  Copy,
  Download,
  Image as ImageIcon,
  LayoutDashboard,
  Link as LinkIcon,
  Lock,
  LogOut,
  Megaphone,
  Rocket,
  Send,
  Share2,
  Sparkles,
  Target,
  Trophy,
  Upload,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api, API, logout as apiLogout, getUser } from "@/lib/api";
import { track } from "@/lib/analytics";
import { Logo } from "@/components/SiteChrome";
import ReviewNotificationBanner from "@/components/dashboard/ReviewNotificationBanner";
import NotificationBell from "@/components/dashboard/NotificationBell";
import ResourcesTab from "@/components/dashboard/ResourcesTab";
import ReshareKit from "@/components/dashboard/ReshareKit";
import { HandleSetupModal, HandleBanner, HandleCard } from "@/components/dashboard/HandleSetup";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONTENT_TYPES = [
  { v: "instagram_post_original", label: "Instagram post / carousel (original)", group: "Original" },
  { v: "linkedin_post_original", label: "LinkedIn post (original)", group: "Original" },
  { v: "twitter_post_original", label: "Twitter / X post (original)", group: "Original" },
  { v: "threads_post_original", label: "Threads post (original)", group: "Original" },
  { v: "instagram_reel", label: "Instagram Reel (original only)", group: "Original" },
  { v: "youtube_video", label: "YouTube video (original, ≥2 min)", group: "Original" },
  { v: "ai_marketing_video", label: "AI marketing video (original only)", group: "Original" },
  { v: "newsletter_mention", label: "Newsletter mention", group: "Brand / Press" },
  { v: "blog_post", label: "Blog post about NoobSync", group: "Brand / Press" },
  { v: "podcast_feature", label: "Podcast feature / mention", group: "Brand / Press" },
  { v: "instagram_story_reshare", label: "Instagram story — reshare NoobSync asset", group: "Reshare" },
  { v: "linkedin_reshare", label: "LinkedIn reshare of NoobSync post", group: "Reshare" },
  { v: "twitter_reshare", label: "Twitter / X reshare (retweet / quote)", group: "Reshare" },
  { v: "whatsapp_share", label: "WhatsApp share of NoobSync poster", group: "Reshare" },
];

const RESHARE_TYPES = new Set(["instagram_story_reshare", "linkedin_reshare", "twitter_reshare", "whatsapp_share"]);
const ORIGINAL_ONLY_HINT = new Set(["instagram_reel", "youtube_video", "ai_marketing_video"]);

function copyText(t, source) {
  navigator.clipboard.writeText(t);
  toast.success("Copied!");
  if (source) {
    track("referral_link_copied", { source });
  }
}

function StatusBadge({ status }) {
  const map = {
    pending: { bg: "bg-amber-500/15 text-amber-300 border-amber-500/40", l: "Pending" },
    approved: { bg: "bg-[#A855F7]/15 text-[#A855F7] border-[#A855F7]/40", l: "Approved" },
    rejected: { bg: "bg-rose-500/15 text-rose-300 border-rose-500/40", l: "Rejected" },
  };
  const s = map[status] || map.pending;
  return <span className={`rounded border px-2 py-0.5 text-xs font-bold uppercase ${s.bg}`}>{s.l}</span>;
}

function PlatformCoverageCard({ data, onGoToProfile }) {
  const ps = data?.platforms_status || {};
  const PLATFORMS = [
    { key: "instagram_handle", label: "Instagram", types: 3 },
    { key: "linkedin_url", label: "LinkedIn", types: 2 },
    { key: "twitter_handle", label: "Twitter / X", types: 2 },
    { key: "threads_handle", label: "Threads", types: 1 },
    { key: "youtube_url", label: "YouTube", types: 1 },
  ];
  const filled = PLATFORMS.filter((p) => ps[p.key]?.locked);
  const empty = PLATFORMS.filter((p) => !ps[p.key]?.locked);
  const filledCount = filled.length;
  const totalCount = PLATFORMS.length;
  const missingTypes = empty.reduce((s, p) => s + p.types, 0);
  if (empty.length === 0) return null; // hide if all filled — no nag

  const goToProfile = () => {
    if (onGoToProfile) { onGoToProfile(); return; }
    if (typeof window !== "undefined") {
      window.location.hash = "profile";
      const trigger = document.querySelector("[data-testid='tab-profile']");
      if (trigger) trigger.click();
    }
  };

  return (
    <div data-testid="platform-coverage" className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.05] via-zinc-950 to-zinc-950 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-300">Platform coverage</p>
          <p className="mt-1 font-display text-lg font-bold text-white">
            You have <span className="text-amber-300">{filledCount}</span> of {totalCount} platforms unlocked
          </p>
          <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800">
            <div
              className="h-full bg-gradient-to-r from-amber-500 to-emerald-400 transition-all"
              style={{ width: `${(filledCount / totalCount) * 100}%` }}
            />
          </div>
          <p className="mt-3 text-xs text-zinc-400">
            Add{" "}
            <strong className="text-zinc-200">{empty.length} more</strong>{" "}
            ({empty.map((p) => p.label).join(", ")}) to unlock{" "}
            <strong className="text-emerald-300">{missingTypes} extra activit{missingTypes === 1 ? "y" : "ies"}</strong>.
          </p>
        </div>
        <button
          onClick={goToProfile}
          data-testid="coverage-cta"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-500 px-4 py-2 text-sm font-bold text-black hover:bg-amber-400 transition"
        >
          <User className="h-4 w-4" /> Add platforms
        </button>
      </div>
    </div>
  );
}

function PrizeBanner({ cohort }) {
  const prizes = cohort?.prizes;
  if (!prizes?.length) return null;
  const cur = cohort?.prize_currency || "₹";
  const fmt = (n) => (typeof n === "number" ? n.toLocaleString("en-IN") : n);
  const total = prizes.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const medals = ["🥇", "🥈", "🥉"];
  const accent = ["#FFD700", "#C0C0C0", "#CD7F32"];
  return (
    <div className="rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/10 via-zinc-950 to-zinc-950 p-5 sm:p-6" data-testid="dash-prize-banner">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">What you&apos;re playing for</p>
          <p className="mt-1 font-display text-2xl sm:text-3xl font-black text-white">{cur}{fmt(total)} in cash prizes</p>
          <p className="mt-1 text-sm text-zinc-400">Top 3 on the leaderboard when the cohort ends take the cash. Keep posting to climb.</p>
        </div>
        <div className="flex gap-3">
          {prizes.slice(0, 3).map((p, i) => (
            <div key={p.rank || i} data-testid={`dash-prize-${i + 1}`} className="rounded-xl border px-4 py-3 text-center" style={{ borderColor: `${accent[i]}55` }}>
              <div className="text-lg">{medals[i]}</div>
              <div className="mt-1 font-mono text-base font-black text-white">{cur}{fmt(p.amount)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FollowUsCard() {
  const [socials, setSocials] = useState({});
  useEffect(() => {
    api.get("/site-settings").then((r) => setSocials(r.data?.socials || {})).catch(() => {});
  }, []);
  const items = [["instagram", "Instagram"], ["youtube", "YouTube"], ["linkedin", "LinkedIn"]].filter(([k]) => socials?.[k]?.url);
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border border-[#22D3EE]/25 bg-gradient-to-br from-[#22D3EE]/[0.06] via-zinc-950 to-zinc-950 p-5 sm:p-6" data-testid="dash-follow-card">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#22D3EE]">Quick access</p>
      <h3 className="mt-1 font-display text-xl font-bold">NoobSync channels</h3>
      <p className="mt-1 text-sm text-zinc-400">Jump straight to our latest posts to reshare, react, or tag along — no digging around. Fresh content to amplify lands here first.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {items.map(([k, label]) => (
          <a
            key={k}
            href={socials[k].url}
            target="_blank"
            rel="noreferrer"
            data-testid={`dash-follow-${k}`}
            className="inline-flex items-center gap-2 rounded-full border border-[#22D3EE]/40 bg-[#22D3EE]/10 px-4 py-2 text-sm font-semibold text-[#22D3EE] transition hover:-translate-y-0.5 hover:bg-[#22D3EE]/20"
          >
            {label} ↗
          </a>
        ))}
      </div>
    </div>
  );
}

function OverviewTab({ data, onRefresh, onGoToProfile, onSetHandle }) {
  if (!data) return null;
  const { ambassador, cohort, rank, referral_link, week_usage, week_usage_pending, weekly_caps, points_config, current_streak, streak_threshold, is_top_10_pct, top_10_pct_threshold, recent_reviews } = data;
  const breakdown = ambassador.points_by_type || {};
  const streak = current_streak || 0;
  const threshold = streak_threshold || 4;
  const streakActive = streak >= threshold;
  const hasPoints = (ambassador.total_points || 0) > 0;
  const isCampus = !!ambassador.is_campus_ambassador;
  const noHandle = !(ambassador.dashboard_handle || "").trim();

  const daysLeft = cohort?.end_date
    ? Math.max(0, Math.ceil((new Date(cohort.end_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <div className="space-y-6">
      <ReviewNotificationBanner items={recent_reviews || []} onDismiss={onRefresh} />
      {noHandle && !isCampus && <HandleBanner onOpen={onSetHandle} />}
      <PrizeBanner cohort={cohort} />
      <FollowUsCard />
      <div className="grid gap-4 sm:gap-5 grid-cols-2 md:grid-cols-3">
        <div className="rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/10 via-zinc-950 to-zinc-950 p-4 sm:p-6 col-span-2 md:col-span-1">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">Your rank</p>
          {isCampus ? (
            <>
              <p data-testid="ov-rank" className="mt-2 font-display text-2xl sm:text-3xl font-black text-[#A855F7]">Referral Champion</p>
              <p data-testid="ov-rank-hint" className="mt-1 text-xs text-zinc-500">Off the public leaderboard — your focus is referrals</p>
            </>
          ) : hasPoints && rank ? (
            <p data-testid="ov-rank" className="mt-2 font-display text-4xl sm:text-5xl font-black text-white">#{rank}</p>
          ) : (
            <>
              <p data-testid="ov-rank" className="mt-2 font-display text-4xl sm:text-5xl font-black text-zinc-500">—</p>
              <p data-testid="ov-rank-hint" className="mt-1 text-xs text-zinc-500">Earn points to get ranked</p>
            </>
          )}
          <p className="mt-1 text-sm text-zinc-400">{cohort?.name || "Cohort"}</p>
          {(cohort?.theme_name || cohort?.was_extended) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {cohort?.theme_name && (
                <span
                  data-testid="ov-theme-chip"
                  className="inline-flex items-center gap-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-300"
                >
                  🎨 {cohort.theme_name}
                </span>
              )}
              {cohort?.was_extended && (
                <span
                  data-testid="ov-extended-chip"
                  className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-300"
                  title="The cohort end date was pushed out based on community feedback."
                >
                  ⏱ Extended on community request
                </span>
              )}
            </div>
          )}
          {(cohort?.sponsors || []).length > 0 && (
            <div data-testid="ov-sponsors" className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-zinc-400">
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Sponsored by</span>
              {(cohort.sponsors || []).map((s, i) => (
                s.website_url ? (
                  <a key={`${s.name || "sponsor"}-${i}`} href={s.website_url} target="_blank" rel="noreferrer" className="font-semibold text-white hover:text-[#A855F7]" title={s.name}>
                    {s.name}
                  </a>
                ) : (
                  <span key={`${s.name || "sponsor"}-${i}`} className="font-semibold text-zinc-200">{s.name}</span>
                )
              ))}
            </div>
          )}
          {is_top_10_pct && (
            <div
              data-testid="ov-top10-badge"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-300"
              title={`Top ${top_10_pct_threshold} of cohort`}
            >
              <Target className="h-3.5 w-3.5" /> Top 10% — {cohort?.name || "Cohort"}
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Total points</p>
          <p data-testid="ov-points" className="mt-2 font-display text-4xl sm:text-5xl font-black text-[#A855F7]">{ambassador.total_points}</p>
          <p className="mt-1 text-xs sm:text-sm text-zinc-400">{ambassador.submission_count} subs • {ambassador.referral_count} refs</p>
        </div>
        <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Days left</p>
          <p className="mt-2 font-display text-4xl sm:text-5xl font-black text-white">{daysLeft ?? "—"}</p>
          <p className="mt-1 text-xs sm:text-sm text-zinc-400">Ends {cohort?.end_date ? new Date(cohort.end_date).toLocaleDateString() : "—"}</p>
        </div>
      </div>

      <div className={`rounded-2xl border p-5 ${streakActive ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-900 bg-zinc-950"}`} data-testid="streak-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Current streak</p>
            <p className="mt-1 font-display text-3xl font-black">
              {streak} week{streak === 1 ? "" : "s"}{streak > 0 && <span className="ml-2">🔥</span>}
            </p>
          </div>
          <div className="text-right text-xs text-zinc-400">
            {streakActive ? (
              <span className="rounded border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-amber-300">+{points_config?.streak_bonus_weekly || 50} pts/week bonus active</span>
            ) : (
              <span>Reach {threshold} weeks to unlock weekly streak bonus</span>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="min-w-0 rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6 lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Your referral code</p>
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <span data-testid="ov-referral-code" className="rounded-md border border-[#A855F7]/30 bg-[#A855F7]/10 px-3 py-2 font-mono text-base sm:text-lg font-black text-[#A855F7] break-all">
              {ambassador.referral_code}
            </span>
            <button data-testid="ov-copy-code" onClick={() => copyText(ambassador.referral_code, "code_button")} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-3 py-2 text-sm hover:border-zinc-700">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Your referral link</p>
          <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <span data-testid="ov-referral-link" className="flex-1 min-w-0 truncate rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-xs text-zinc-300">
              {referral_link}
            </span>
            <button data-testid="ov-copy-link" onClick={() => copyText(referral_link, "overview_card")} className="inline-flex w-full sm:w-auto items-center justify-center gap-1 rounded-md border border-zinc-800 px-3 py-2 text-sm hover:border-zinc-700">
              <Copy className="h-4 w-4" /> Copy
            </button>
          </div>
          <p className="mt-3 text-xs text-zinc-500">
            One code, multiple products. Pick the right product link from the <strong className="text-zinc-300">Referrals tab</strong> based on what you&apos;re sharing — each product has its own points payout.
          </p>
        </div>
        <div className="min-w-0 rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Points by activity</p>
          <ul className="mt-3 space-y-2 text-sm">
            {Object.entries(breakdown).length === 0 && (
              <li className="text-zinc-500">No points yet — start submitting content.</li>
            )}
            {Object.entries(breakdown).map(([k, v]) => (
              <li key={k} className="flex items-center justify-between">
                <span className="text-zinc-400">{k.replace(/_/g, " ")}</span>
                <span className="font-mono font-bold text-[#A855F7]">{v}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <PlatformCoverageCard data={data} onGoToProfile={onGoToProfile} />

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Weekly activity</p>
          <p className="text-xs text-zinc-500">
            <span className="text-zinc-300">Core</span> activities are the fastest way to climb. <span className="text-zinc-300">Creator-track</span> items are optional but worth more — pick whichever fits your style.
          </p>
        </div>

        {(() => {
          // Server-driven Core/Creator classification per cohort (falls back to sensible defaults)
          const CORE_KEYS = (data.core_types && data.core_types.length) ? data.core_types : [
            "instagram_post_original",
            "linkedin_post_original",
            "twitter_post_original",
            "twitter_reshare",
            "threads_post_original",
            "instagram_story_reshare",
            "linkedin_reshare",
            "whatsapp_share",
            "newsletter_mention",
          ];
          const CREATOR_KEYS = (data.creator_types && data.creator_types.length) ? data.creator_types : [
            "instagram_reel",
            "youtube_video",
            "ai_marketing_video",
            "blog_post",
            "podcast_feature",
          ];
          // Build a quick lookup of locked types + the field that unlocks each
          const lockedSet = new Set(data.locked_content_types || []);
          const lockedUnlocks = data.locked_unlocks || {};
          const PLATFORM_LABELS = {
            instagram_handle: "Instagram",
            linkedin_url: "LinkedIn",
            youtube_url: "YouTube",
            twitter_handle: "Twitter / X",
            threads_handle: "Threads",
          };

          const renderLockedTile = (k) => {
            const pts = (points_config || {})[k] ?? 0;
            const field = lockedUnlocks[k];
            const platformLabel = PLATFORM_LABELS[field] || "platform";
            const label = (k || "").replace(/_/g, " ");
            return (
              <button
                key={k}
                data-testid={`tile-locked-${k}`}
                onClick={() => { if (onGoToProfile) onGoToProfile(); else { window.location.hash = "profile"; document.querySelector("[data-testid='tab-profile']")?.click(); } }}
                className="rounded-xl border border-zinc-800 bg-zinc-950/40 p-4 text-left transition hover:border-amber-500/40 hover:bg-amber-500/[0.04]"
                title={`Add your ${platformLabel} to unlock this`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="font-display text-sm font-bold capitalize text-zinc-400 line-clamp-2">{label}</p>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-300">
                    <Lock className="h-3 w-3" /> Locked
                  </span>
                </div>
                <p className="mt-2 text-xs text-zinc-500">Add <strong className="text-amber-200">{platformLabel}</strong> in Profile to unlock</p>
                {pts > 0 && <p className="mt-2 text-xs text-zinc-600">Earn up to <span className="font-mono text-amber-300">+{pts} pts</span> each</p>}
              </button>
            );
          };
          const renderTile = (k) => {
            if (lockedSet.has(k)) return renderLockedTile(k);
            const cap = (weekly_caps || {})[k] ?? 0;
            const pts = (points_config || {})[k] ?? 0;
            if (!cap && !pts) return null;
            const totalUsed = week_usage?.[k] || 0;
            const pendingCount = (week_usage_pending || {})[k] || 0;
            const approvedCount = Math.max(0, totalUsed - pendingCount);
            const approvedPct = cap > 0 ? Math.min(100, (approvedCount / cap) * 100) : 0;
            const pendingPct = cap > 0 ? Math.min(100, (pendingCount / cap) * 100) : 0;
            const maxThisWeek = (cap || 0) * (pts || 0);
            return (
              <div key={k} data-testid={`week-tile-${k}`} className="rounded-md border border-zinc-800 bg-zinc-900 p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="capitalize text-zinc-300">{k.replace(/_/g, " ")}</span>
                  <span className="font-mono text-zinc-400">{totalUsed}/{cap}</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  <span className="font-mono font-bold text-[#A855F7]">+{pts} pts each</span>
                  <span className="text-zinc-500">max {maxThisWeek}/wk</span>
                </div>
                <div className="relative mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                  <div className="absolute left-0 top-0 h-full bg-[#A855F7]" style={{ width: `${approvedPct}%` }} title={`${approvedCount} approved`} />
                  <div className="absolute top-0 h-full bg-[#A855F7]/30" style={{ left: `${approvedPct}%`, width: `${pendingPct}%` }} title={`${pendingCount} pending review`} />
                </div>
                <div className="mt-1.5 flex items-center justify-between text-[10px] text-zinc-500">
                  <span><span className="text-emerald-300">{approvedCount}</span> approved · earns points</span>
                  {pendingCount > 0 && <span className="text-amber-300">{pendingCount} pending review</span>}
                </div>
              </div>
            );
          };
          const knownKeys = new Set([...CORE_KEYS, ...CREATOR_KEYS, "referral"]);
          const otherKeys = Object.keys(weekly_caps || {}).filter((k) => !knownKeys.has(k));
          // Show ALL tiles — locked ones render in greyed "Add platform" state.
          // Only hide tiles that are completely disabled (not locked AND not available).
          const availSet = new Set(data.available_content_types || []);
          const isShown = (k) => {
            if (lockedSet.has(k)) return true;
            return availSet.size === 0 ? true : availSet.has(k);
          };
          const visibleCore = CORE_KEYS.filter(isShown);
          const visibleCreator = CREATOR_KEYS.filter(isShown);
          const visibleOther = otherKeys.filter(isShown);
          return (
            <div className="mt-4 space-y-5">
              {visibleCore.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-300">
                    <Rocket className="h-3.5 w-3.5" /> Core · Recommended for everyone
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visibleCore.map(renderTile)}
                  </div>
                </div>
              )}
              {visibleCreator.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#A855F7]">
                    <Sparkles className="h-3.5 w-3.5" /> Creator track · Optional, high value
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {visibleCreator.map(renderTile)}
                  </div>
                </div>
              )}
              {visibleOther.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleOther.map(renderTile)}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

function SubmitTab({ overview, onSubmitted, prefillReshare = 0 }) {
  const [mode, setMode] = useState("original"); // "original" | "reshare"
  const [type, setType] = useState("instagram_post_original");
  const [proofUrl, setProofUrl] = useState("");
  const [originalAssetUrl, setOriginalAssetUrl] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]); // up to 10 carousel slides
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [postUrlDrafts, setPostUrlDrafts] = useState({}); // {subId: url}
  const urlRef = useRef(null);

  const load = () => api.get("/ambassador/content/mine").then((r) => setSubmissions(r.data.items));

  useEffect(() => { load(); }, []);

  // "I posted this" nudge from the Reshare Kit — flip to reshare mode + focus the live URL field.
  useEffect(() => {
    if (prefillReshare > 0) {
      switchMode("reshare");
      setTimeout(() => { urlRef.current?.focus(); urlRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, 120);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillReshare]);

  const isReshare = RESHARE_TYPES.has(type);
  const isOriginalOnly = ORIGINAL_ONLY_HINT.has(type);

  // When mode flips, pick the first content type of that group so dropdown stays valid
  const switchMode = (m) => {
    setMode(m);
    if (m === "reshare") {
      const first = CONTENT_TYPES.find((c) => c.group === "Reshare");
      if (first) setType(first.v);
    } else {
      const first = CONTENT_TYPES.find((c) => c.group === "Original");
      if (first) setType(first.v);
    }
    setProofUrl(""); setOriginalAssetUrl(""); setNote(""); setFiles([]);
  };

  // Visible groups for the dropdown based on selected mode
  const visibleGroups = mode === "reshare" ? ["Reshare"] : ["Original", "Brand / Press"];
  // Only show content types whose platform the ambassador actually has filled
  const availSet = new Set(overview?.available_content_types || []);
  const filterAvail = (c) => availSet.size === 0 ? true : availSet.has(c.v);

  const submit = async (e) => {
    e.preventDefault();
    if (!proofUrl && files.length === 0) {
      toast.error(isReshare
        ? "Add your repost URL or upload a screenshot"
        : "Share a draft link or upload up to 10 images");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("content_type", type);
      if (proofUrl) fd.append("proof_url", proofUrl);
      if (originalAssetUrl) fd.append("original_asset_url", originalAssetUrl);
      if (note) fd.append("note", note);
      files.forEach((f) => fd.append("files", f));
      await api.post("/ambassador/content/submit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(isReshare ? "Submitted for review" : "Draft submitted — we'll review it soon");
      track("content_submitted", { content_type: type, is_reshare: isReshare, slides: files.length });
      setProofUrl(""); setOriginalAssetUrl(""); setNote(""); setFiles([]);
      load();
      onSubmitted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  const addFiles = (selected) => {
    const incoming = Array.from(selected || []);
    setFiles((prev) => [...prev, ...incoming].slice(0, 10));
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const savePostUrl = async (subId) => {
    const url = (postUrlDrafts[subId] || "").trim();
    if (!url) {
      toast.error("Paste your live post URL first");
      return;
    }
    try {
      await api.patch(`/ambassador/content/${subId}/post-url`, { post_url: url });
      toast.success("Live post URL saved");
      setPostUrlDrafts((d) => ({ ...d, [subId]: "" }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const used = overview?.week_usage?.[type] || 0;
  const cap = overview?.weekly_caps?.[type] || 0;
  const points = overview?.points_config?.[type] || 0;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <form onSubmit={submit} className="space-y-4 rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6 lg:col-span-2">
        <div>
          <h3 className="font-display text-xl font-bold">Submit content</h3>
          <p className="mt-1 text-xs text-zinc-500">Pick the flow that matches what you did this week.</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-xl border border-zinc-900 bg-zinc-900/40 p-1.5">
          <button
            type="button"
            data-testid="submit-mode-original"
            onClick={() => switchMode("original")}
            className={`rounded-lg px-3 py-2.5 text-left transition ${mode === "original" ? "bg-[#A855F7] text-white shadow-lg shadow-[#A855F7]/30" : "text-zinc-400 hover:bg-zinc-900/70"}`}
          >
            <div className="text-sm font-bold">📝 Original content</div>
            <div className={`mt-0.5 text-[11px] ${mode === "original" ? "text-white/85" : "text-zinc-500"}`}>Submit a draft for review — points awarded after approval.</div>
          </button>
          <button
            type="button"
            data-testid="submit-mode-reshare"
            onClick={() => switchMode("reshare")}
            className={`rounded-lg px-3 py-2.5 text-left transition ${mode === "reshare" ? "bg-[#A855F7] text-white shadow-lg shadow-[#A855F7]/30" : "text-zinc-400 hover:bg-zinc-900/70"}`}
          >
            <div className="text-sm font-bold">🔁 Reshare</div>
            <div className={`mt-0.5 text-[11px] ${mode === "reshare" ? "text-white/85" : "text-zinc-500"}`}>You reshared a NoobSync post / asset — submit the live URL.</div>
          </button>
        </div>

        <div className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 text-xs text-zinc-400">
          {mode === "original" ? (
            <>Submit a post <strong className="text-zinc-200">you created from scratch</strong> (Instagram post, LinkedIn post, Reel, YouTube video, Blog, Podcast, etc.). Earns full original points.</>
          ) : (
            <>Submit proof of a <strong className="text-zinc-200">reshare</strong> — e.g. you reposted a NoobSync Story or shared a NoobSync LinkedIn post. Link the original NoobSync asset below. Earns reshare points.</>
          )}
        </div>

        {mode === "original" ? (
          <div data-testid="submit-callout-original" className="flex gap-3 rounded-lg border border-amber-500/40 bg-amber-500/[0.08] p-3 text-xs text-amber-200">
            <span className="mt-0.5 text-base">⚠️</span>
            <div className="space-y-1">
              <p className="font-bold text-amber-100">Submit your draft first — don&apos;t post publicly yet.</p>
              <p>Share a draft link (Google Doc, Figma, unlisted video link, Notion, etc.) <strong>or upload a screenshot</strong>. Once admin approves, post it live and add the live URL from the &quot;Your submissions&quot; panel to claim points. Posting before approval risks point forfeiture if it doesn&apos;t align with brand guidelines.</p>
            </div>
          </div>
        ) : (
          <div data-testid="submit-callout-reshare" className="flex gap-3 rounded-lg border border-emerald-400/40 bg-emerald-500/[0.08] p-3 text-xs text-emerald-200">
            <span className="mt-0.5 text-base">✓</span>
            <div className="space-y-1">
              <p className="font-bold text-emerald-100">Post freely, then claim points.</p>
              <p>Reshares of approved NoobSync content (from <strong>Resources</strong> or our official handles) can be posted right away — just submit the live URL here for review and points.</p>
            </div>
          </div>
        )}

        <div>
          <label className="mb-2 block text-sm font-semibold text-zinc-300">Content type</label>
          <Select key={mode} value={type} onValueChange={setType}>
            <SelectTrigger data-testid="submit-type" className="border-zinc-800 bg-zinc-900">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {visibleGroups.map((g) => (
                <div key={g}>
                  <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">{g}</div>
                  {CONTENT_TYPES.filter((c) => c.group === g).filter(filterAvail).map((c) => (
                    <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>
                  ))}
                </div>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-zinc-500">
            <span>Weekly cap: <span className="font-mono text-zinc-300">{used}/{cap}</span></span>
            <span className="hidden sm:inline">•</span>
            <span>Earn: <span className="font-mono text-[#A855F7]">+{points} pts</span></span>
          </div>
          {isOriginalOnly && (
            <p className="mt-2 rounded border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
              Original content only — reshares of existing videos are not eligible for this category.
            </p>
          )}
          {type === "instagram_post_original" && (
            <p className="mt-2 rounded border border-[#A855F7]/30 bg-[#A855F7]/10 px-3 py-2 text-xs text-[#A855F7]" data-testid="carousel-hint">
              📸 Posting a <strong>carousel</strong>? This is the option to pick — then add each slide (up to 10 images) in the <strong>&ldquo;carousel slides&rdquo;</strong> field below. A carousel counts as one Instagram post.
            </p>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-zinc-300">
            {isReshare
              ? "Where you shared (your story / repost URL)"
              : "Draft link "}
            {!isReshare && <span className="text-zinc-500 text-xs font-normal">(Google Doc, Figma, Notion, unlisted YouTube, etc. — optional if uploading a screenshot)</span>}
          </label>
          <input
            data-testid="submit-url"
            ref={urlRef}
            value={proofUrl}
            onChange={(e) => setProofUrl(e.target.value)}
            className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]"
            placeholder={isReshare ? "https://instagram.com/yourstory/..." : "https://docs.google.com/..."}
          />
          {!isReshare && (
            <p className="mt-1.5 text-[11px] text-zinc-500">
              You&apos;ll add the <strong className="text-zinc-300">live post URL</strong> after admin approves your draft.
            </p>
          )}
        </div>

        {isReshare && (
          <div data-testid="submit-original-asset">
            <label className="mb-2 block text-sm font-semibold text-zinc-300">Link to the original NoobSync post you reshared <span className="text-zinc-500 text-xs">(optional)</span></label>
            <input data-testid="submit-original-url" value={originalAssetUrl} onChange={(e) => setOriginalAssetUrl(e.target.value)} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]" placeholder="https://instagram.com/noobsync/..." />
          </div>
        )}

        <div>
          <label className="mb-2 block text-sm font-semibold text-zinc-300">
            {isReshare ? "Screenshot (backup proof)" : "Screenshots / carousel slides"}
            <span className="ml-1 text-zinc-500 text-xs font-normal">{isReshare ? "(optional)" : "(optional if draft link provided — up to 10 images)"}</span>
          </label>
          <input
            data-testid="submit-file"
            type="file"
            multiple
            accept="image/*,application/pdf,video/mp4,video/quicktime,video/webm"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
            disabled={files.length >= 10}
            className="block w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-[#A855F7] file:px-3 file:py-1 file:text-xs file:font-bold file:text-black disabled:opacity-50"
          />
          <p className="mt-1.5 text-[11px] text-zinc-500">
            Posting a multi-image carousel? Add each slide here ({files.length}/10). For very large sets, paste a Google Drive / shared-folder link in the field above instead.
          </p>
          {files.length > 0 && (
            <div data-testid="submit-file-previews" className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-5">
              {files.map((f, i) => {
                const isImg = f.type?.startsWith("image/");
                const url = isImg ? URL.createObjectURL(f) : null;
                return (
                  <div key={i} className="group relative aspect-square overflow-hidden rounded-md border border-zinc-800 bg-zinc-900">
                    {isImg ? (
                      <img src={url} alt={`slide ${i + 1}`} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-zinc-500">{f.name}</div>
                    )}
                    <span className="absolute left-1 top-1 rounded bg-black/70 px-1 text-[10px] font-bold text-white">{i + 1}</span>
                    <button
                      type="button"
                      data-testid={`remove-file-${i}`}
                      onClick={() => removeFile(i)}
                      className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white opacity-0 transition group-hover:opacity-100"
                      aria-label="Remove image"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label className="mb-2 block text-sm font-semibold text-zinc-300">Note (optional)</label>
          <textarea data-testid="submit-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:border-[#A855F7] focus:outline-none focus:ring-1 focus:ring-[#A855F7]" />
        </div>

        <button data-testid="submit-content-btn" disabled={loading} className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-md bg-[#A855F7] px-5 py-2.5 text-sm font-bold text-white hover:-translate-y-0.5 transition-all disabled:opacity-60">
          <Send className="h-4 w-4" />{isReshare ? "Submit for review" : "Submit draft for review"}
        </button>
      </form>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
        <h3 className="font-display text-lg font-bold">Your submissions</h3>
        <ul className="mt-4 max-h-[500px] space-y-3 overflow-y-auto" data-testid="submission-list">
          {submissions.length === 0 && <li className="text-sm text-zinc-500">No submissions yet.</li>}
          {submissions.map((s) => {
            const isOriginal = !["instagram_story_reshare", "linkedin_reshare", "twitter_reshare", "whatsapp_share"].includes(s.content_type);
            const needsPostUrl = isOriginal && s.status === "approved" && !s.post_url;
            return (
              <li key={s.id} data-testid={`submission-${s.id}`} className="rounded-md border border-zinc-800 bg-zinc-900 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-display font-semibold capitalize text-xs sm:text-sm">{s.content_type.replace(/_/g, " ")}</span>
                  <StatusBadge status={s.status} />
                </div>
                <p className="mt-1 text-xs text-zinc-500">{new Date(s.submitted_at).toLocaleString()}</p>
                {s.proof_url && (
                  <a href={s.proof_url} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-zinc-400 hover:text-[#A855F7] hover:underline">
                    <span className="text-zinc-500">{isOriginal ? "Draft: " : "Posted: "}</span>{s.proof_url}
                  </a>
                )}
                {(s.proof_file_paths?.length > 0 || s.proof_file_path) && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`sub-thumbs-${s.id}`}>
                    {(s.proof_file_paths?.length ? s.proof_file_paths : [s.proof_file_path]).slice(0, 10).map((p, i) => (
                      <a key={i} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" className="relative block h-12 w-12 overflow-hidden rounded border border-zinc-800 bg-zinc-950">
                        {/\.(jpe?g|png|webp|gif|svg)$/i.test(p) ? (
                          <img src={`${API}/files/${p}`} alt={`slide ${i + 1}`} className="h-full w-full object-cover" />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-[9px] text-zinc-500">file</span>
                        )}
                      </a>
                    ))}
                    {s.proof_file_paths?.length > 1 && <span className="self-center text-[10px] text-zinc-500">{s.proof_file_paths.length} slides</span>}
                  </div>
                )}
                {s.post_url && (
                  <a href={s.post_url} target="_blank" rel="noreferrer" className="mt-1 block truncate text-xs text-[#A855F7] hover:underline">
                    <span className="text-emerald-400">✓ Live: </span>{s.post_url}
                  </a>
                )}
                {s.status === "approved" && <p className="mt-1 text-xs text-[#A855F7]">+{s.points_awarded} pts</p>}
                {s.status === "rejected" && s.rejection_reason && <p className="mt-1 text-xs text-rose-300">{s.rejection_reason}</p>}

                {needsPostUrl && (
                  <div data-testid={`add-post-url-${s.id}`} className="mt-2 rounded border border-emerald-500/40 bg-emerald-500/10 p-2">
                    <p className="text-[11px] font-bold text-emerald-200">🎉 Approved — post it live, then paste the URL:</p>
                    <div className="mt-1.5 flex flex-col sm:flex-row gap-1.5">
                      <input
                        value={postUrlDrafts[s.id] || ""}
                        onChange={(e) => setPostUrlDrafts((d) => ({ ...d, [s.id]: e.target.value }))}
                        placeholder="https://instagram.com/..."
                        className="flex-1 rounded border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs focus:border-emerald-400 focus:outline-none"
                        data-testid={`post-url-input-${s.id}`}
                      />
                      <button
                        type="button"
                        onClick={() => savePostUrl(s.id)}
                        data-testid={`post-url-save-${s.id}`}
                        className="rounded bg-emerald-500 px-3 py-1 text-xs font-bold text-black hover:bg-emerald-400"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function ReferralsTab({ referralCode }) {
  const [history, setHistory] = useState({ items: [], total_points: 0, count: 0 });
  const [products, setProducts] = useState([]);
  const [env, setEnv] = useState("prod");

  useEffect(() => {
    api.get("/ambassador/referrals/mine").then((r) => setHistory(r.data));
    api.get("/referral-products").then((r) => {
      setProducts(r.data.products || []);
      setEnv(r.data.environment || "prod");
    });
  }, []);

  const buildLink = (url) => {
    if (!url) return "";
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}ref=${encodeURIComponent(referralCode || "")}`;
  };
  const copy = (text, source) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
    if (source) track("referral_link_copied", { source });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-[#A855F7]/30 bg-gradient-to-br from-[#A855F7]/5 via-zinc-950 to-zinc-950 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A855F7]">Your referral code</p>
            <p className="mt-1 font-mono text-2xl font-black text-white" data-testid="ref-code">{referralCode || "—"}</p>
          </div>
          <div className="flex items-center gap-2">
            {env === "test" && (
              <span data-testid="ref-env-badge" className="rounded-full border border-amber-400/50 bg-amber-500/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-300" title="Admin set the platform to dev/test mode — links below point to staging URLs.">
                Dev links
              </span>
            )}
            <button
              onClick={() => copy(referralCode || "", "referrals_tab_code")}
              data-testid="ref-copy-code"
              className="rounded-md border border-zinc-800 bg-zinc-900 px-4 py-2 text-sm font-semibold hover:border-[#A855F7]/60"
            >
              Copy code
            </button>
          </div>
        </div>
        <p className="mt-3 text-xs text-zinc-500">
          One code, multiple products. Pick the right link below for whichever NoobSync product you&apos;re sharing.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2" data-testid="ref-products">
        {products.length === 0 && (
          <p className="text-sm text-zinc-500 md:col-span-2">No referral products are active right now.</p>
        )}
        {products.map((p) => {
          const link = buildLink(p.product_url);
          return (
            <div key={p.product_key} data-testid={`ref-product-${p.product_key}`} className="rounded-2xl border border-zinc-900 bg-zinc-950 p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h4 className="font-display text-lg font-bold text-white">{p.product_name}</h4>
                  {p.description && <p className="mt-1 text-xs text-zinc-400">{p.description}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {p.signup_points > 0 && (
                    <span className="rounded-full border border-[#A855F7]/40 bg-[#A855F7]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#C084FC]">
                      +{p.signup_points} signup
                    </span>
                  )}
                  {p.conversion_points > 0 && (
                    <span className="rounded-full border border-[#A855F7]/60 bg-[#A855F7]/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#E9D5FF]">
                      +{p.conversion_points} {p.conversion_event_label || "conversion"}
                    </span>
                  )}
                </div>
              </div>
              <div className="mt-4 flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/80 px-3 py-2">
                <code className="flex-1 truncate text-xs text-zinc-300">{link}</code>
                <button
                  onClick={() => copy(link, `product_${p.product_key}`)}
                  data-testid={`ref-copy-${p.product_key}`}
                  className="rounded border border-zinc-700 px-2 py-1 text-xs font-semibold text-zinc-200 hover:border-[#A855F7]/60"
                >
                  Copy link
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Referral history</h3>
          <div className="text-right">
            <p className="text-xs text-zinc-500">Total points</p>
            <p className="font-mono text-xl font-black text-[#A855F7]" data-testid="ref-total">+{history.total_points}</p>
          </div>
        </div>
        <ul className="mt-5 divide-y divide-zinc-800" data-testid="ref-list">
          {history.items.length === 0 && (
            <li className="py-4 text-sm text-zinc-500">No referrals yet — share a product link above to start earning.</li>
          )}
          {history.items.map((r) => (
            <li key={r.id} className="flex items-center justify-between py-3">
              <div>
                <p className="font-display font-semibold capitalize">{r.product_key || r.platform}</p>
                <p className="text-xs text-zinc-500">{r.referred_email || r.referred_user_id || "anonymous"}{r.converted ? " · converted" : ""}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-sm font-bold text-[#A855F7]">+{(r.points_awarded || 0) + (r.conversion_bonus_awarded || 0)}</p>
                <p className="text-xs text-zinc-500">{new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function LbTab({ ambassadorId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/leaderboard?limit=10&ambassador_id=${encodeURIComponent(ambassadorId || "")}`).then((r) => setData(r.data));
  }, [ambassadorId]);
  if (!data) return null;
  const COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const my = data.my_rank;
  const ranked = (data.leaderboard || []).filter((r) => r.ranked);
  const DASH_LB_THRESHOLD = data.dashboard_reveal_threshold ?? 5;
  const unlocked = (data.active_with_points || 0) >= DASH_LB_THRESHOLD;

  // Locked state — mirror the public site's "warming up" teaser. No 0-point roster,
  // no discouraging "N yet to score" count. Unlocks once 5 ambassadors have scored.
  if (!unlocked) {
    return (
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <h3 className="font-display text-xl font-bold">Cohort leaderboard</h3>
        <div className="mt-5 rounded-2xl border border-dashed border-zinc-800 bg-zinc-950/60 p-12 text-center" data-testid="lb-tab-warming-up">
          <Trophy className="mx-auto h-12 w-12 text-zinc-700" />
          <p className="mt-4 font-display text-2xl text-zinc-200">The leaderboard is warming up</p>
          <p className="mt-2 text-sm text-zinc-500">
            The Top 10 goes live once the cohort gets rolling. Be one of the first names on the board — post content or refer a signup to start scoring.
          </p>
          {my?.ranked && (
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-xs font-semibold text-[#A855F7]" data-testid="lb-tab-my-points">
              You already have {my.points} pts — you&apos;re on your way 🚀
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-xl font-bold">Cohort leaderboard · Top 10</h3>
        {data.total_active_ambassadors > 10 && (
          <span className="text-xs text-zinc-500">of {data.total_active_ambassadors} ambassadors</span>
        )}
      </div>

      {my && (!my.ranked || !my.in_visible_window) && (
        <div className="mt-4 rounded-xl border border-[#A855F7]/40 bg-[#A855F7]/10 p-4" data-testid="lb-tab-my-rank">
          <p className="text-[10px] font-bold uppercase tracking-wider text-[#A855F7]">YOUR POSITION</p>
          {my.ranked ? (
            <>
              <div className="mt-1 flex items-baseline justify-between">
                <p className="font-display text-3xl font-black text-white">
                  #{my.rank}<span className="ml-2 text-base font-normal text-zinc-400">of {my.total}</span>
                </p>
                <p className="font-mono text-2xl font-bold text-[#A855F7]">{my.points}<span className="ml-1 text-xs text-zinc-500">pts</span></p>
              </div>
              <p className="mt-1 text-xs text-zinc-400">
                {my.is_top_10_pct
                  ? "🔥 You're in the top 10% of the cohort. Keep pushing — winners are picked from here."
                  : `Climb ${Math.max(1, (ranked[9]?.points || 0) - my.points + 1)} more points to break into the top 10.`}
              </p>
            </>
          ) : (
            <>
              <p className="mt-1 font-display text-2xl font-black text-white">Not ranked yet</p>
              <p className="mt-1 text-xs text-zinc-400">Earn your first points to claim a spot on the board — post content or refer a signup. Everyone starts here.</p>
            </>
          )}
        </div>
      )}

      <ul className="mt-5 divide-y divide-zinc-800" data-testid="lb-tab-list">
        {ranked.map((row, i) => {
          const isMe = row.ambassador_id === ambassadorId;
          const color = i < 3 ? COLORS[i] : null;
          return (
            <li key={row.ambassador_id} className={`flex items-center justify-between px-3 py-3 ${isMe ? "rounded-md bg-[#A855F7]/10 ring-1 ring-[#A855F7]/40" : ""}`}>
              <div className="flex items-center gap-3">
                <span className="w-8 font-mono text-sm" style={color ? { color } : { color: "#71717a" }}>#{row.rank}</span>
                <div>
                  <p className="font-display font-semibold">{row.display_name} {isMe && <span className="ml-1 text-xs text-[#A855F7]">(you)</span>}</p>
                  <p className="text-xs text-zinc-500">{row.display_subtitle || ""}</p>
                </div>
              </div>
              <span className="font-mono text-base font-bold" style={color ? { color } : { color: "#A855F7" }}>{row.points}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ResourcesTab_LEGACY_REMOVED() { return null; }
// ResourcesTab moved to /components/dashboard/ResourcesTab.jsx

function ProfileTab({ overview, onRefresh }) {
  const amb = overview?.ambassador || {};
  const platformsStatus = overview?.platforms_status || {};
  const [application, setApplication] = useState(null);
  useEffect(() => {
    api.get("/ambassador/me/application").then((r) => setApplication(r.data)).catch(() => {});
  }, []);
  const SOCIALS = [
    { k: "instagram_handle", label: "Instagram handle", placeholder: "yourhandle" },
    { k: "linkedin_url", label: "LinkedIn URL", placeholder: "https://linkedin.com/in/..." },
    { k: "youtube_url", label: "YouTube URL", placeholder: "https://youtube.com/@..." },
    { k: "twitter_handle", label: "Twitter / X handle", placeholder: "yourhandle" },
    { k: "threads_handle", label: "Threads handle", placeholder: "yourhandle" },
  ];

  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);

  const lockedCount = SOCIALS.filter((s) => platformsStatus[s.k]?.locked).length;
  const unlockedFields = SOCIALS.filter((s) => !platformsStatus[s.k]?.locked);

  const saveDrafts = async () => {
    const payload = {};
    for (const s of unlockedFields) {
      const v = (drafts[s.k] || "").trim();
      if (v) payload[s.k] = v;
    }
    if (Object.keys(payload).length === 0) {
      toast.error("Fill at least one new platform");
      return;
    }
    setSaving(true);
    try {
      const res = await api.patch("/ambassador/me/profile", payload);
      toast.success(`${(res.data?.updated || []).length} platform(s) added & locked`);
      setDrafts({});
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl font-bold">Your details</h3>
          {(() => {
            const persona = application?.self_description || amb.self_description;
            return persona && persona !== "none" ? (
              <span data-testid="profile-persona-badge" className="rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#A855F7]">{persona}</span>
            ) : null;
          })()}
        </div>
        {(() => {
          const app = application || {};
          const persona = app.self_description || amb.self_description || "";
          const PERSONA_FIELDS = {
            "Student": [["College", "college"], ["Year of study", "year_of_study"], ["Course / Branch", "course"]],
            "Recent Graduate": [["College / University", "college"], ["Graduation year", "graduation_year"], ["Course / Degree", "course"]],
            "Working Professional": [["Company", "company_name"], ["Role / Title", "role_title"], ["Industry", "industry"]],
            "Entrepreneur": [["Startup / Company", "company_name"], ["Industry", "industry"], ["Stage", "startup_stage"]],
            "Content Creator": [["Content niche", "content_niche"], ["Primary platform", "primary_platform"], ["Instagram followers", "instagram_followers", "num"]],
            "Influencer": [["Content niche", "content_niche"], ["Primary platform", "primary_platform"], ["Instagram followers", "instagram_followers", "num"]],
            "Brand / Company": [["Brand / Company name", "brand_name"], ["Website", "brand_website", "link"], ["Industry", "industry"], ["Community / audience size", "brand_audience_size"]],
            "Other": [["About", "custom_description"]],
          };
          const personaFields = PERSONA_FIELDS[persona] || [];
          const rows = [
            ["Full name", app.full_name || amb.full_name],
            ["Email", app.email || amb.email],
            ["City & state", app.city_state || amb.city_state],
            ...personaFields.map(([label, key, type]) => {
              const v = app[key];
              let node = v || null;
              if (v && type === "link") node = <a href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="text-[#A855F7] hover:underline">{v}</a>;
              else if (v && type === "num") node = Number(v).toLocaleString();
              return [label, node];
            }),
            ["Referral code", amb.referral_code],
          ];
          const isBrand = persona === "Brand / Company";
          const logo = app.brand_logo_path;
          return (
            <>
              <dl className="mt-4 space-y-2 text-sm">
                {rows.map(([k, v], i) => (
                  <div key={`${k}-${i}`} data-testid={`profile-detail-${String(k).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-baseline justify-between gap-4 border-b border-zinc-900 py-2">
                    <dt className="shrink-0 text-zinc-500">{k}</dt>
                    <dd className="break-all text-right font-mono text-zinc-200">{v || <span className="text-zinc-600">—</span>}</dd>
                  </div>
                ))}
              </dl>
              {isBrand && logo && (
                <div className="mt-4">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-zinc-500">Brand logo</p>
                  <img data-testid="profile-brand-logo" src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${logo}`} alt="Brand logo" className="h-20 w-20 rounded-lg border border-zinc-800 object-contain bg-zinc-900 p-1" />
                </div>
              )}
            </>
          );
        })()}
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6">
        <div className="flex items-start gap-2">
          <Lock className="mt-0.5 h-4 w-4 text-zinc-500" />
          <div className="flex-1">
            <h3 className="font-display text-xl font-bold">Your platforms</h3>
            <p className="mt-1 text-xs text-zinc-500">
              <span className="text-zinc-300">{lockedCount}</span> locked ·
              <span className="text-emerald-300"> {unlockedFields.length}</span> open to add.
              Once you fill an empty platform here, it locks. Need to change a locked one? Email{" "}
              <a href="mailto:info@noobsync.com" className="text-[#A855F7]">info@noobsync.com</a>.
            </p>
          </div>
        </div>

        <dl className="mt-4 space-y-3 text-sm">
          {SOCIALS.map((s) => {
            const st = platformsStatus[s.k] || {};
            const locked = !!st.locked;
            return (
              <div key={s.k} data-testid={`profile-${s.k}`} className={`rounded-md border px-3 py-2.5 ${locked ? "border-zinc-800 bg-zinc-900/70" : "border-emerald-500/30 bg-emerald-500/[0.04]"}`}>
                <div className="flex items-center justify-between">
                  <dt className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">{s.label}</dt>
                  {locked ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                      <Lock className="h-2.5 w-2.5" /> Locked
                    </span>
                  ) : (
                    <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300">+ Add</span>
                  )}
                </div>
                {locked ? (
                  <dd className="mt-1 break-all font-mono text-sm text-zinc-200">{st.value || <span className="text-zinc-600">—</span>}</dd>
                ) : (
                  <input
                    data-testid={`profile-input-${s.k}`}
                    value={drafts[s.k] || ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [s.k]: e.target.value }))}
                    placeholder={s.placeholder}
                    className="mt-1 w-full rounded border border-zinc-800 bg-zinc-950 px-2 py-1.5 font-mono text-xs text-white outline-none focus:border-emerald-400"
                  />
                )}
              </div>
            );
          })}
        </dl>

        {unlockedFields.length > 0 && (
          <button
            onClick={saveDrafts}
            disabled={saving}
            data-testid="save-platforms-btn"
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-emerald-500 px-4 py-2 text-sm font-bold text-black hover:bg-emerald-400 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save new platforms"}
          </button>
        )}
      </div>

      <HandleCard handle={amb.dashboard_handle} changeCount={amb.handle_change_count || 0} onSaved={onRefresh} />
      <ChangePasswordCard />
    </div>
  );
}

function ChangePasswordCard() {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8 || !/[a-zA-Z]/.test(newPw) || !/[0-9]/.test(newPw)) {
      toast.error("Password must be at least 8 characters and include a letter and a number");
      return;
    }
    if (newPw !== confirmPw) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password updated");
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "mt-1 w-full rounded border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-white outline-none focus:border-[#A855F7]";

  return (
    <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-6 lg:col-span-2" data-testid="change-password-card">
      <div className="flex items-start gap-2">
        <Lock className="mt-0.5 h-4 w-4 text-zinc-500" />
        <div>
          <h3 className="font-display text-xl font-bold">Password &amp; security</h3>
          <p className="mt-1 text-xs text-zinc-500">Update your sign-in password. You can also sign in with a one-time email code any time.</p>
        </div>
      </div>
      <form onSubmit={submit} className="mt-4 grid gap-4 sm:grid-cols-3">
        <label className="block text-sm">
          <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Current password</span>
          <input data-testid="cp-old" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="Current password" autoComplete="current-password" className={inputCls} />
        </label>
        <label className="block text-sm">
          <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">New password</span>
          <input data-testid="cp-new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Min 8 chars · letter + number" autoComplete="new-password" className={inputCls} />
        </label>
        <label className="block text-sm">
          <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Confirm new password</span>
          <input data-testid="cp-confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" className={inputCls} />
        </label>
        <div className="sm:col-span-3">
          <button data-testid="cp-submit" disabled={saving} className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:-translate-y-0.5 transition-transform disabled:opacity-50">
            {saving ? "Updating…" : "Update password"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const initialTabRaw = (typeof window !== "undefined" && window.location.hash.replace("#", "")) || "overview";
  const validTabsSet = new Set(["overview", "submit", "reshare", "referrals", "leaderboard", "resources", "profile"]);
  const [tab, setTab] = useState(validTabsSet.has(initialTabRaw) ? initialTabRaw : "overview");
  const [reshareNudge, setReshareNudge] = useState(0);
  const [handleModalOpen, setHandleModalOpen] = useState(false);
  const user = getUser();
  const nav = useNavigate();

  useEffect(() => {
    if (!user || user.role !== "ambassador") {
      nav("/login");
      return;
    }
    refresh();
  }, []);

  // Prompt on every dashboard load (i.e. every login) until a handle is set —
  // unconfigured ambassadors are hidden from the public leaderboard.
  useEffect(() => {
    const amb = overview?.ambassador;
    if (!amb) return;
    if (!(amb.dashboard_handle || "").trim() && !amb.is_campus_ambassador) {
      setHandleModalOpen(true);
    }
  }, [overview?.ambassador?.id]);

  const refresh = () => api.get("/ambassador/me/overview").then((r) => setOverview(r.data)).catch(() => {});

  const logout = async () => { await apiLogout(); nav("/"); };

  if (!overview) return <div className="p-10 text-center text-zinc-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-30 border-b border-zinc-900 bg-zinc-950/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
          <div className="flex items-center gap-3 sm:gap-5 min-w-0">
            <Logo />
            <span className="hidden text-xs font-bold uppercase tracking-[0.2em] text-zinc-500 md:inline">Brand Ambassador</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 text-sm">
            <span data-testid="dash-user-name" className="hidden text-zinc-300 md:inline">Hi {overview.ambassador.full_name.split(" ")[0]} 👋</span>
            <NotificationBell items={overview.recent_reviews || []} onDismiss={refresh} />
            {user?.content_manager && (
              <button data-testid="dash-goto-cm" onClick={() => nav("/content-manager")} className="inline-flex items-center gap-1 rounded-md border border-[#A855F7]/40 bg-[#A855F7]/10 px-2 sm:px-3 py-1.5 text-xs font-semibold text-[#A855F7] hover:bg-[#A855F7]/20">
                <LayoutDashboard className="h-3 w-3" /> <span className="hidden sm:inline">Content Manager</span><span className="sm:hidden">CM</span>
              </button>
            )}
            <button data-testid="dash-logout" onClick={logout} className="inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2 sm:px-3 py-1.5 text-xs text-zinc-300 hover:border-zinc-700">
              <LogOut className="h-3 w-3" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-3 py-5 sm:px-5 sm:py-8">
        <Tabs value={tab} onValueChange={(v) => { setTab(v); if (typeof window !== "undefined") window.history.replaceState(null, "", `#${v}`); }}>
          <TabsList data-testid="dash-tabs" className="mb-6 flex w-full overflow-x-auto sm:grid sm:grid-cols-7 gap-1 rounded-md border border-zinc-900 bg-zinc-950 p-1 scrollbar-thin">
            <TabsTrigger value="overview" data-testid="tab-overview" className="shrink-0 sm:shrink"><LayoutDashboard className="mr-1 h-4 w-4" />Overview</TabsTrigger>
            <TabsTrigger value="submit" data-testid="tab-submit" className="shrink-0 sm:shrink"><Send className="mr-1 h-4 w-4" />Submit</TabsTrigger>
            <TabsTrigger value="reshare" data-testid="tab-reshare" className="shrink-0 sm:shrink"><Share2 className="mr-1 h-4 w-4" />Reshare Kit</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="tab-referrals" className="shrink-0 sm:shrink"><LinkIcon className="mr-1 h-4 w-4" />Referrals</TabsTrigger>
            <TabsTrigger value="leaderboard" data-testid="tab-lb" className="shrink-0 sm:shrink"><Trophy className="mr-1 h-4 w-4" />Leaderboard</TabsTrigger>
            <TabsTrigger value="resources" data-testid="tab-resources" className="shrink-0 sm:shrink"><Megaphone className="mr-1 h-4 w-4" />Resources</TabsTrigger>
            <TabsTrigger value="profile" data-testid="tab-profile" className="shrink-0 sm:shrink"><User className="mr-1 h-4 w-4" />Profile</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab data={overview} onRefresh={refresh} onGoToProfile={() => setTab("profile")} onSetHandle={() => setHandleModalOpen(true)} /></TabsContent>
          <TabsContent value="submit"><SubmitTab overview={overview} onSubmitted={refresh} prefillReshare={reshareNudge} /></TabsContent>
          <TabsContent value="reshare"><ReshareKit referralCode={overview.ambassador.referral_code} referralLink={overview.referral_link} ambassadorName={overview.ambassador.full_name} onPostedToInstagram={() => { setReshareNudge((n) => n + 1); setTab("submit"); if (typeof window !== "undefined") window.history.replaceState(null, "", "#submit"); }} /></TabsContent>
          <TabsContent value="referrals"><ReferralsTab referralCode={overview.ambassador.referral_code} /></TabsContent>
          <TabsContent value="leaderboard"><LbTab ambassadorId={overview.ambassador.id} /></TabsContent>
          <TabsContent value="resources"><ResourcesTab referralCode={overview.ambassador.referral_code} referralLink={overview.referral_link} ambassadorName={overview.ambassador.full_name} /></TabsContent>
          <TabsContent value="profile"><ProfileTab overview={overview} onRefresh={refresh} /></TabsContent>
        </Tabs>
      </div>
      <HandleSetupModal
        open={handleModalOpen}
        currentHandle={overview.ambassador.dashboard_handle}
        changeCount={overview.ambassador.handle_change_count || 0}
        onClose={() => setHandleModalOpen(false)}
        onSaved={refresh}
      />
    </div>
  );
}
